import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { fmtData, numRef } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { OrigemPicker } from "@/components/FornTag";
import { Sparkles, ShoppingCart, AlertCircle } from "lucide-react";
import { toast } from "sonner";

export default function PorEncomendar() {
  const [linhas, setLinhas] = useState([]);
  const [fornecedores, setFornecedores] = useState([]);

  const load = () => api.get("/por-encomendar").then((r) => setLinhas(r.data));
  useEffect(() => {
    load();
    api.get("/fornecedores").then((r) => setFornecedores(r.data));
  }, []);

  const setFornecedor = async (it, fornecedor_id, origem_id = null) => {
    try {
      await api.post(`/atendimentos/${it.atendimento_id}/linhas/${it.linha_id}/fornecedor`, { fornecedor_id, origem_id });
      toast.success(origem_id ? "Fornecedor e armazém definidos." : "Fornecedor definido.");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const gerar = async () => {
    try {
      const { data } = await api.post("/encomendas/gerar");
      toast.success(`${data.criadas} criadas · ${data.atualizadas} atualizadas · ${data.linhas} linhas`);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const semOrigem = (l) => !l.origem_id && (fornecedores.find((f) => f.id === l.fornecedor_id)?.origens || []).length > 1;
  const semForn = linhas.filter((l) => !l.fornecedor_id || semOrigem(l));
  const comForn = linhas.filter((l) => l.fornecedor_id && !semOrigem(l));

  const Linha = ({ it }) => (
    <tr className="border-t border-slate-100" data-testid={`pe-linha-${it.atendimento_numero}-${it.linha_id.slice(0, 6)}`}>
      <td className="px-4 py-2 font-medium whitespace-nowrap">{numRef(it.atendimento_numero, it.atendimento_codigo)}</td>
      <td className="px-4 py-2">{it.descricao}</td>
      <td className="px-4 py-2 font-mono text-slate-500">{it.ref_marca || "—"}</td>
      <td className="px-4 py-2 text-center">{it.qtd}</td>
      <td className="px-4 py-2 text-slate-600 truncate max-w-[10rem]">{it.cliente || "—"}</td>
      <td className="px-4 py-2 text-slate-400 whitespace-nowrap">{fmtData(it.data)}</td>
      <td className="px-4 py-2">
        <OrigemPicker fornecedores={fornecedores} fornecedorId={it.fornecedor_id} origemId={it.origem_id}
          onChange={(fid, oid) => setFornecedor(it, fid, oid)} placeholder="Escolher fornecedor / armazém"
          className="h-8 w-48 text-xs" testid={`pe-forn-${it.atendimento_numero}-${it.linha_id.slice(0, 6)}`} />
      </td>
    </tr>
  );

  return (
    <div className="max-w-5xl mx-auto w-full min-w-0">
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Por encomendar</h1>
          <p className="text-xs text-slate-400">Linhas aprovadas ainda não enviadas a fornecedor. Defina o fornecedor e serão consolidadas automaticamente por período.</p>
        </div>
        <Button className="bg-indigo-600 hover:bg-indigo-700 gap-1.5" data-testid="btn-gerar-pe" onClick={gerar}>
          <Sparkles size={15} /> Gerar encomendas pendentes
        </Button>
      </div>

      {semForn.length > 0 && (
        <div className="bg-white rounded-lg border border-amber-200 shadow-sm overflow-hidden mb-4">
          <div className="bg-amber-50 border-b border-amber-100 px-4 py-2.5 flex items-center gap-2">
            <AlertCircle size={15} className="text-amber-600" />
            <span className="text-sm font-semibold text-amber-800">Falta fornecedor ou origem ({semForn.length}) — escolha para poderem ser encomendadas</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-500 uppercase text-[10px]">
                <tr>
                  <th className="text-left px-4 py-1.5 font-semibold">Pedido</th>
                  <th className="text-left px-4 py-1.5 font-semibold">Descrição</th>
                  <th className="text-left px-4 py-1.5 font-semibold">Ref.</th>
                  <th className="text-center px-4 py-1.5 font-semibold">Qtd</th>
                  <th className="text-left px-4 py-1.5 font-semibold">Cliente</th>
                  <th className="text-left px-4 py-1.5 font-semibold">Data</th>
                  <th className="text-left px-4 py-1.5 font-semibold">Fornecedor / armazém</th>
                </tr>
              </thead>
              <tbody>{semForn.map((it) => <Linha key={it.linha_id} it={it} />)}</tbody>
            </table>
          </div>
        </div>
      )}

      {linhas.length > 0 && <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
        <div className="bg-slate-50 border-b border-slate-100 px-4 py-2.5 flex items-center gap-2">
          <ShoppingCart size={15} className="text-indigo-600" />
          <span className="text-sm font-semibold text-slate-700">Com fornecedor ({comForn.length}) — entram na próxima consolidação</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-500 uppercase text-[10px]">
              <tr>
                <th className="text-left px-4 py-1.5 font-semibold">Pedido</th>
                <th className="text-left px-4 py-1.5 font-semibold">Descrição</th>
                <th className="text-left px-4 py-1.5 font-semibold">Ref.</th>
                <th className="text-center px-4 py-1.5 font-semibold">Qtd</th>
                <th className="text-left px-4 py-1.5 font-semibold">Cliente</th>
                <th className="text-left px-4 py-1.5 font-semibold">Data</th>
                <th className="text-left px-4 py-1.5 font-semibold">Fornecedor / armazém</th>
              </tr>
            </thead>
            <tbody>{comForn.map((it) => <Linha key={it.linha_id} it={it} />)}</tbody>
          </table>
        </div>
        {!comForn.length && <div className="text-xs text-slate-400 text-center py-6">Nada por encomendar com fornecedor.</div>}
      </div>}

      {!linhas.length && <div className="text-sm text-slate-400 text-center py-12 bg-white rounded-lg border border-dashed border-slate-300 mt-4">Nada por encomendar. Aprove linhas num pedido para aparecerem aqui.</div>}
    </div>
  );
}
